"use client";

import { useRef, useEffect } from "react";
import type { TravelAgency } from "@/lib/findAgent";
import styles from "./find-agent.module.css";

const PER_PAGE = 5;
const MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAP_ID || "DEMO_MAP_ID";

interface AgentMapViewProps {
  center: { lat: number; lng: number };
  agencies: TravelAgency[];
  currentPage: number;
}

export default function AgentMapView({
  center,
  agencies,
  currentPage,
}: AgentMapViewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapInstance.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: 12,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      mapId: MAP_ID,
    });
  }, [center]);

  useEffect(() => {
    const map = mapInstance.current;
    if (!map) return;
    markersRef.current.forEach((m) => (m.map = null));
    markersRef.current = [];

    // User location marker (red circle)
    const userPin = document.createElement("div");
    userPin.style.cssText =
      "width:20px;height:20px;background:#e84118;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.3);";

    const userMarker = new google.maps.marker.AdvancedMarkerElement({
      position: center,
      map,
      content: userPin,
      title: "Your Location",
    });
    markersRef.current.push(userMarker);

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(center);
    const infoWindow = new google.maps.InfoWindow();

    agencies.forEach((agency, i) => {
      const loc = agency.locationInformation?.[0]?.tnAddress;
      if (!loc?.lat || !loc?.lng) return;
      const pos = { lat: loc.lat, lng: loc.lng };

      // Numbered pin
      const pin = document.createElement("div");
      pin.style.cssText =
        "display:flex;align-items:center;justify-content:center;width:28px;height:28px;background:#e84118;border-radius:50%;color:#fff;font-size:12px;font-weight:bold;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);cursor:pointer;";
      pin.textContent = `${i + 1 + PER_PAGE * (currentPage - 1)}`;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: pos,
        map,
        content: pin,
        title: agency.name,
      });

      marker.addListener("click", () => {
        const phone =
          agency.locationInformation[0]?.phone1 ||
          agency.locationInformation[0]?.phone2 ||
          "";
        infoWindow.setContent(
          `<div style="padding:8px 4px;font-family:inherit;">
            <strong style="font-size:14px;">${agency.name}</strong>
            ${loc.full_Address ? `<br/><span style="color:#6b7280;font-size:13px;">${loc.full_Address}</span>` : ""}
            ${phone ? `<br/><span style="color:#e84118;font-size:13px;"><i class="fa fa-phone"></i> ${phone}</span>` : ""}
          </div>`
        );
        infoWindow.open({ anchor: marker, map });
      });
      markersRef.current.push(marker);
      bounds.extend(pos);
    });

    if (agencies.length > 0) {
      map.fitBounds(bounds);
      const listener = google.maps.event.addListener(map, "idle", () => {
        const z = map.getZoom();
        if (z && z > 15) map.setZoom(15);
        google.maps.event.removeListener(listener);
      });
    }
  }, [agencies, center, currentPage]);

  return <div ref={mapRef} className={styles.map} />;
}
